import { apiClient } from './client';

export type Market = 'US' | 'MX' | 'CA';
export const MARKETS: Market[] = ['US', 'MX', 'CA'];
export type MrlUnit = 'mg/kg' | 'ppm' | 'ppb';
export const MRL_UNITS: MrlUnit[] = ['mg/kg', 'ppm', 'ppb'];

export interface ComplianceRule {
  ruleId: string;
  productId: string;
  productName?: string;
  crop: string;
  market: Market;
  phiDays: number;
  reiHours?: number;
  mrlMgPerKg: number;
  mrlValue?: number;
  mrlUnit?: string;
  notes?: string;
  scope: 'TENANT' | 'GLOBAL';
  editable: boolean;
  verifiedBy?: string;
  verifiedAt?: string;
  updatedAt?: string;
}

export interface ComplianceRuleInput {
  productId: string;
  crop: string;
  market: Market;
  phiDays: number;
  reiHours: number;
  mrl: number;
  mrlUnit: MrlUnit;
  notes?: string;
}

export interface ComplianceImportRow {
  row: number;
  productId?: string;
  productName?: string;
  crop?: string;
  market?: string;
  phiDays?: number;
  reiHours?: number;
  mrlValue?: number;
  mrlUnit?: string;
  action?: 'CREATE' | 'UPDATE';
  errors: { field: string; message: string }[];
}

export interface ComplianceImportPreview {
  rows: ComplianceImportRow[];
  summary: { total: number; valid: number; errors: number; toCreate: number; toUpdate: number };
}

export interface ComplianceImportResult { created: number; updated: number; failed: number[] }

export const fetchComplianceRules = async (filters: { productId?: string; crop?: string; market?: string } = {}): Promise<ComplianceRule[]> => {
  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => !!v));
  return (await apiClient.get('/api/compliance-rules', { params: Object.keys(params).length ? params : undefined })).data.rules;
};

export const createComplianceRule = async (input: ComplianceRuleInput): Promise<ComplianceRule> =>
  (await apiClient.post('/api/compliance-rules', input)).data.rule;

export const updateComplianceRule = async (ruleId: string, input: ComplianceRuleInput): Promise<ComplianceRule> =>
  (await apiClient.put(`/api/compliance-rules/${ruleId}`, input)).data.rule;

export const deleteComplianceRule = async (ruleId: string): Promise<void> => {
  await apiClient.delete(`/api/compliance-rules/${ruleId}`);
};

export const fetchComplianceTemplate = async (): Promise<{ fileName: string; fileBase64: string }> =>
  (await apiClient.get('/api/compliance-rules/import/template')).data;

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const previewComplianceImport = async (file: File): Promise<ComplianceImportPreview> =>
  (await apiClient.post('/api/compliance-rules/import/preview', { fileBase64: await fileToBase64(file), fileName: file.name })).data;

/** El servidor vuelve a leer y validar el archivo: no se le envía el preview. */
export const confirmComplianceImport = async (file: File): Promise<ComplianceImportResult> =>
  (await apiClient.post('/api/compliance-rules/import/confirm', { fileBase64: await fileToBase64(file), fileName: file.name })).data;
